// Directional Influence — Quick-Time DI window (Smash-style, adapted for turn-based)
export const DI = {
  // Window timing
  WINDOW_DURATION: 1.5,          // seconds — the Quick-Time window
  // Angle mechanics
  MAX_ANGLE_SHIFT: Math.PI / 12, // 15° max trajectory shift
  PERPENDICULAR_THRESHOLD: 0.2,  // dot product threshold for "perpendicular enough"
  ACCESSIBILITY_FLOOR: 0.8,      // any perpendicular input gives 80% of max shift

  // Minimum knockback to trigger DI window (skip for gentle bumps)
  MIN_KNOCKBACK_SPEED: 4.0,      // total speed threshold — below this, no DI window

  // Multiplayer authority timeout — if di_resolve never arrives, fall through to local
  AUTHORITY_TIMEOUT: 3.0,        // seconds after timer expires before local fallback

  // Visual — trajectory arc
  ARC_COLOR: 0x00ffff,
  ARC_ALPHA: 0.6,
  ARC_DOT_COUNT: 24,             // dots in trajectory preview
  ARC_DOT_RADIUS: 3.5,
  ARC_GLOW_RADIUS: 7,            // soft glow behind each dot
  ARC_GLOW_ALPHA: 0.2,

  // Visual — directional arrow (shows held direction)
  ARROW_LENGTH: 55,
  ARROW_WIDTH: 3,
  ARROW_HEAD_SIZE: 10,
  ARROW_GLOW_STRENGTH: 6,

  // Ghost position (predicted landing)
  GHOST_ALPHA: 0.25,
  GHOST_PULSE_SPEED: 8,          // pulse speed for ghost opacity
  GHOST_PULSE_AMP: 0.12,         // amplitude of pulse

  // Ring indicator (perpendicular axis + input direction)
  RING_RADIUS: 45,
  RING_WIDTH: 2.5,
  RING_SEGMENTS: 48,
  RING_ACTIVE_COLOR: 0x00ff88,
  RING_INACTIVE_COLOR: 0x333355,

  // Prompt text
  PROMPT_FONT_SIZE: 18,
  PROMPT_Y_OFFSET: -75,          // above tank

  // Timer bar
  TIMER_BAR_WIDTH: 60,
  TIMER_BAR_HEIGHT: 4,
  TIMER_BAR_Y_OFFSET: -60,
  TIMER_BAR_BG_COLOR: 0x222244,
  TIMER_BAR_FILL_COLOR: 0x00ffcc,
  TIMER_BAR_CRITICAL_COLOR: 0xff4444,
  TIMER_BAR_CRITICAL_THRESHOLD: 0.3, // fraction of time remaining

  // Screen effects
  VIGNETTE_ALPHA: 0.25,          // darkened edges during DI window
  ENTRY_FLASH_ALPHA: 0.12,       // flash when DI window opens
  ENTRY_FLASH_COLOR: 0x00ffff,
  EXIT_FLASH_ALPHA: 0.08,        // flash when DI resolves

  // Particles (Sakurai: crisp + contrast, not just ADD glow)
  SPARK_COUNT: 8,                // burst sparks when DI window opens
  SPARK_SPEED_MIN: 2,
  SPARK_SPEED_MAX: 6,
  SPARK_LIFE: 12,
  SPARK_SIZE: 4,
  RESOLVE_SPARK_COUNT: 12,       // sparks when DI resolves (directional fan)
  RESOLVE_SPARK_SPEED: 8,
  RESOLVE_SPARK_LIFE: 10,
} as const;

// Momentum Burst — offensive turn-start impulse
export const BURST = {
  // Window timing
  WINDOW_DURATION: 1.5,            // seconds — matches DI window duration
  AUTHORITY_TIMEOUT: 3.0,          // seconds — matches DI timeout

  // Impulse
  IMPULSE_SPEED: 8.0,             // explosive momentum impulse — covers real ground
  UPWARD_MOMENTUM_COST: 60,       // momentum deducted for upward burst directions
  HORIZONTAL_MOMENTUM_COST: 60,   // momentum deducted for horizontal burst directions
  BRACE_MOMENTUM_COST: 60,        // momentum deducted for brace (grounded down)

  // Anticipation (pre-resolve compression)
  ANTICIPATION_DURATION: 0.35,    // seconds before burst resolves — longer = more readable
  ANTICIPATION_TARGET: 0.60,      // deep squash target (matches DI's LAUNCH_FX depth)

  // Visual — gold/kinetic palette
  GOLD: 0xffbb00,
  GOLD_BRIGHT: 0xffcc00,
  GOLD_DARK: 0x443300,
  AMBER: 0xff9900,
  AMBER_DARK: 0x6b3a00,

  // Ring
  RING_RADIUS: 45,
  RING_WIDTH: 2.5,
  RING_SEGMENTS: 48,
  RING_ACTIVE_COLOR: 0xffbb00,
  RING_INACTIVE_COLOR: 0x443322,

  // Arrow
  ARROW_LENGTH: 55,
  ARROW_WIDTH: 3,
  ARROW_HEAD_SIZE: 10,

  // Trajectory arc
  ARC_COLOR: 0xffbb00,
  ARC_ALPHA: 0.6,
  ARC_DOT_COUNT: 24,
  ARC_DOT_RADIUS: 3.5,
  ARC_GLOW_RADIUS: 7,
  ARC_GLOW_ALPHA: 0.2,

  // Ghost position
  GHOST_ALPHA: 0.25,
  GHOST_PULSE_SPEED: 8,
  GHOST_PULSE_AMP: 0.12,

  // Timer bar
  TIMER_BAR_WIDTH: 60,
  TIMER_BAR_HEIGHT: 4,
  TIMER_BAR_BG_COLOR: 0x332200,
  TIMER_BAR_FILL_COLOR: 0xffbb00,
  TIMER_BAR_CRITICAL_COLOR: 0xff4444,
  TIMER_BAR_CRITICAL_THRESHOLD: 0.3,

  // Prompt text
  PROMPT_FONT_SIZE: 18,
  PROMPT_Y_OFFSET: -75,

  // Screen effects
  VIGNETTE_ALPHA: 0.22,          // close to DI's 0.25 — dramatic, focused
  ENTRY_FLASH_ALPHA: 0.18,
  ENTRY_FLASH_COLOR: 0xffbb00,
  EXIT_FLASH_ALPHA: 0.15,

  // Entry shake
  ENTRY_SHAKE: 6,
  ENTRY_SHAKE_DECAY: 0.78,

  // Resolve shake (scales with whether impulse was applied)
  RESOLVE_SHAKE_NONE: 3,
  RESOLVE_SHAKE_FULL: 14,
  RESOLVE_SHAKE_DECAY: 0.82,

  // Squash on resolve — deep compress then explosive spring (matches DI LAUNCH_FX)
  RESOLVE_SQUASH: 0.55,
  RESOLVE_IMPULSE: 0.55,

  // Particles
  SPARK_COUNT: 8,
  SPARK_SPEED_MIN: 2,
  SPARK_SPEED_MAX: 6,
  SPARK_LIFE: 12,
  SPARK_SIZE: 4,
  RESOLVE_SPARK_COUNT: 12,
  RESOLVE_SPARK_SPEED: 8,
  RESOLVE_SPARK_LIFE: 10,

  // Rocketpack (upward burst visual)
  ROCKET_FLAME_COLOR: 0xff6600,
  ROCKET_SMOKE_COLOR: 0x1a0a00,

  // Post-burst tumble — shorter than knockback tumble so player can aim near apex
  TUMBLE_HITSTUN_PER_SPEED: 10,  // frames per speed unit (vs TUMBLE.HITSTUN_PER_SPEED = 24)
  TUMBLE_MIN_HITSTUN: 20,        // ~0.33s minimum
  TUMBLE_MAX_HITSTUN: 75,        // ~1.25s cap

  // Control Snap — "snap to attention" effect at post-launch ragdoll exit
  CONTROL_SNAP_SQUASH: 0.82,
  CONTROL_SNAP_IMPULSE: 0.15,
  CONTROL_FLASH_BRIGHT: 0xffcc00,
  CONTROL_FLASH_DARK: 0x443300,
  CONTROL_FLASH_DURATION: 0.3,
  CONTROL_SCREEN_FLASH_ALPHA: 0.10,
  CONTROL_SHAKE: 4,

  // Burst Air Control — Smash-style DI during tumble + velocity steering after tumble
  TUMBLE_DI_ACCEL: 0.08,            // per-frame velocity nudge during hitstun (75 frames × 0.08 = 6.0 max, 75% of burst impulse)
  TUMBLE_DI_VERTICAL_MULT: 0.5,     // vertical DI weaker than horizontal (prevents trivially canceling gravity)
  AIR_CONTROL_ACCEL: 0.20,          // per-frame accel after tumble exits (while burstFlying)
  AIR_CONTROL_OPPOSING_MULT: 2.5,   // strong bonus when opposing current vx — 0.50/frame opposing, ~16 frames to zero from 8.0
} as const;

// Burst flight trail — gold-palette speed-tiered trail during post-burst airborne flight
export const BURST_TRAIL = {
  MIN_SPEED: 2.0,             // matches ragdoll threshold — trail shows at any real speed
  TIER_MED: 4.0,
  TIER_HIGH: 6.0,
  COOLDOWN_LOW: 3,            // frames between spawns (was 4 — denser)
  COOLDOWN_MED: 2,
  COOLDOWN_HIGH: 1,           // every frame at high speed — thick trail
  // Wisps (ADD blend, soft glow — gold palette)
  WISP_SCALE_MIN: 0.35,
  WISP_SCALE_MAX: 0.7,
  WISP_LIFE_MIN: 0.1,
  WISP_LIFE_MAX: 0.25,
  WISP_ALPHA: 0.8,
  // Dark smoke (NORMAL blend, soft glow)
  SMOKE_COLOR: 0x0a0a14,
  SMOKE_SCALE_MIN: 0.6,
  SMOKE_SCALE_MAX: 1.2,
  SMOKE_LIFE_MIN: 0.18,
  SMOKE_LIFE_MAX: 0.35,
  SMOKE_ALPHA: 0.55,
  // Bright streaks (ADD blend, hard glow, stretched)
  STREAK_STRETCH: 3.5,
  STREAK_SCALE: 0.35,
  STREAK_LIFE: 0.14,
  STREAK_ALPHA: 0.85,
  // Impact lines (geometric, crisp)
  LINE_EXPAND: 50,
  LINE_WIDTH: 2.0,
  LINE_LIFE: 0.1,
  // Aura glow (soft particle around tank, speed-scaled alpha)
  AURA_SCALE: 2.8,
  AURA_LIFE: 0.12,
  AURA_ALPHA_MIN: 0.25,
  AURA_ALPHA_MAX: 0.5,
  // Afterimage (high tier — soft ghost at previous position)
  AFTERIMAGE_SCALE: 2.0,
  AFTERIMAGE_LIFE: 0.18,
  AFTERIMAGE_ALPHA: 0.35,
  // Rocketpack exhaust (upward bursts — vy below threshold)
  ROCKET_VY_THRESHOLD: -0.5,  // lower threshold — more generous rocketpack trigger
  ROCKET_FLAME_COLOR: 0xff6600,
  ROCKET_SMOKE_COLOR: 0x1a0a00,
} as const;

// Defensive Movement — two-phase recovery after DI knockback
export const DEFENSE = {
    LANDING_BASE_FRAMES: 120,        // ~2.0s at 60fps
    LANDING_DAMAGE_DIVISOR: 200,     // damage% divisor for landing duration scaling
    LANDING_DAMAGE_FLOOR: 0.3,       // min damage multiplier (high damage still gets 30% of base)
    // Phase transitions
    PHASE_TRANSITION_FREEZE_MS: 150, // brief freeze between defenders
    // Colors (player-tinted)
    BAR_TINT_ALPHA: 0.8,
    // Brake (momentum negate) — tight window at recovery start
    BRAKE_WINDOW_FRAMES: 5,        // ~83ms — tight, rewarding
    BRAKE_FLASH_COLOR: 0xffffff,
    BRAKE_DARK: 0x440000,
    BRAKE_SHAKE: 16,
    BRAKE_SHAKE_DECAY: 0.85,
    BRAKE_ANNOUNCE_HOLD: 0.5,
} as const;

// Brace — burst window grounded heal (pure down + grounded = heal 3%, floor 10%)
export const BRACE = {
    HEAL_AMOUNT: 3,
    DAMAGE_FLOOR: 10,
    GREEN_BRIGHT: 0x44ff88,
    GREEN: 0x00ff66,
    GREEN_DARK: 0x003311,
    FLASH_COLOR: 0x00ff66,
    FLASH_ALPHA: 0.20,
    SHAKE: 5,
    SHAKE_DECAY: 0.80,
    SQUASH: 0.88,
    SQUASH_VEL: 0.08,
    HEAL_FLASH_DURATION: 0.5,
    HEAL_FLASH_COLOR: 0x44ff88,
} as const;

// Edgeguard — railgun follow-up phase after DI (hitstun-driven window)
export const EDGEGUARD = {
  ENTRY_FLASH_COLOR: 0x00ffff,
  ENTRY_FLASH_ALPHA: 0.15,
  ENTRY_SHAKE: 4,
  ENTRY_SHAKE_DECAY: 0.78,
  AIM_ASSIST_ANGLE: Math.PI / 12,     // ~15° capture zone
  AIM_ASSIST_STRENGTH_KB: 0.03,       // keyboard pull per frame
  AIM_ASSIST_STRENGTH_GP: 0.06,       // gamepad/touch pull per frame
  AIM_ASSIST_STRENGTH_MOUSE: 0,       // none for mouse
  BEAM_CARVE_TERRAIN: false,          // beam passes through terrain without destroying it
  BEAM_TERMINUS_CRATER: false,        // no crater at beam terminus
} as const;
